import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Download, ChevronDown } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { Alert } from "../../../components/Alert";
import { ColumnPicker } from "../../../components/table/ColumnPicker";
import { OrdersIcon } from "../../../components/icons/OrdersIcon";
import { useOrderTableState, COLUMN_DENSITY_LEVELS } from "../hooks/useOrderTableState";
import { useOrdersActions } from "../hooks/useOrdersActions";

const ACTIONS_COLUMN_KEY = "__actions__";
const TABLE_SCROLL_CLASS = "orders-table-scroll";
const MIN_COLUMN_WIDTH = 10;

export function OrderList({ orders, onOrderDeleted, loading, error }) {
  const { authToken, AUTH_SCHEME } = useAuth();
  const tableState = useOrderTableState(orders);
  const orderActions = useOrdersActions({ authToken, authScheme: AUTH_SCHEME, onOrderDeleted });

  const [filtersOpen, setFiltersOpen] = useState(true);
  const tableScrollRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const [tableClientWidth, setTableClientWidth] = useState(0);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const legacyStyle = document.getElementById("orders-table-scroll-style");
    if (legacyStyle) legacyStyle.remove();
  }, []);

  useLayoutEffect(() => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const measure = () => {
      setTableScrollWidth(scroller.scrollWidth);
      setTableClientWidth(scroller.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [tableState.visibleColumnDefs, tableState.tableRows.length, tableState.filteredRows.length, filtersOpen, tableState.columnSizing.columnWidths]);

  const showStickyScrollbar = tableScrollWidth > tableClientWidth + 1;

  useLayoutEffect(() => {
    const scroller = tableScrollRef.current;
    const sticky = stickyScrollRef.current;
    if (!scroller || !sticky) return undefined;
    const syncSticky = () => {
      if (Math.abs(sticky.scrollLeft - scroller.scrollLeft) > 1) {
        sticky.scrollLeft = scroller.scrollLeft;
      }
    };
    const syncTable = () => {
      if (Math.abs(scroller.scrollLeft - sticky.scrollLeft) > 1) {
        scroller.scrollLeft = sticky.scrollLeft;
      }
    };
    scroller.addEventListener("scroll", syncSticky, { passive: true });
    sticky.addEventListener("scroll", syncTable, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", syncSticky);
      sticky.removeEventListener("scroll", syncTable);
    };
  }, [tableScrollWidth, tableClientWidth]);

  useLayoutEffect(() => {
    if (!showStickyScrollbar) return;
    const scroller = tableScrollRef.current;
    const sticky = stickyScrollRef.current;
    if (scroller && sticky) sticky.scrollLeft = scroller.scrollLeft;
  }, [showStickyScrollbar]);

  const handleFilterChange = (name) => (event) => {
    const { value } = event.target;
    tableState.setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const settingsPanel = buildSettingsPanel({
    filtersOpen,
    setFiltersOpen,
    tableState,
    handleFilterChange,
  });

  let mainContent = null;
  if (loading) {
    mainContent = (
      <div className="text-center text-gray-600">
        <p className="text-lg font-semibold">Loading orders...</p>
      </div>
    );
  } else if (error) {
    mainContent = (
      <div className="text-center">
        <Alert kind="error">{error}</Alert>
      </div>
    );
  } else if (tableState.tableRows.length === 0) {
    mainContent = (
      <div className="text-center text-gray-600">
        <p className="text-lg font-semibold mb-2">No orders have been added.</p>
        <p>Start an Allegro import from Account Settings to add orders.</p>
      </div>
    );
  } else if (tableState.filteredRows.length === 0) {
    mainContent = (
      <div className="space-y-6">
        {settingsPanel}
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white/70 px-6 py-10 text-center text-gray-600">
          <p className="text-lg font-semibold mb-2">No results for the selected settings.</p>
          <p>Change the filters or reset settings to view data.</p>
        </div>
      </div>
    );
  } else {
    mainContent = (
      <div className="relative space-y-4">
        {settingsPanel}
        <div
          ref={tableScrollRef}
          className={`overflow-x-auto ${showStickyScrollbar ? "pb-8" : ""} ${TABLE_SCROLL_CLASS} orders-table-scroll-main`}
        >
          <div className="inline-block min-w-full align-top">
            <OrdersTable
              columnSizing={tableState.columnSizing}
              density={tableState.density}
              filteredRows={tableState.filteredRows}
              visibleColumnDefs={tableState.visibleColumnDefs}
              onRemove={orderActions.removeOrder}
              deleting={orderActions.deleteLoading}
            />
          </div>
        </div>
        {showStickyScrollbar && (
          <div className="pointer-events-none sticky bottom-0 left-0 right-0 bg-white/95 py-1 shadow-[0_-8px_12px_-10px_rgba(15,23,42,0.4)]">
            <div
              ref={stickyScrollRef}
              className={`${TABLE_SCROLL_CLASS} scrollbar pointer-events-auto overflow-x-auto overflow-y-hidden`}
              style={{ height: 16 }}
            >
              <div style={{ width: tableScrollWidth, height: 1 }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-col gap-3 flex-shrink-0 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-2 text-gray-800">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
            <OrdersIcon size={22} />
          </span>
          <h2 className="text-2xl font-bold">Orders</h2>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={orderActions.handleDownload}
            disabled={orderActions.downloading}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
          >
            <Download size={16} />
            {orderActions.downloading ? "Downloading..." : "Download CSV"}
          </button>
        </div>
      </div>

      {orderActions.message && (
        <div className="flex-shrink-0">
          <Alert kind={orderActions.message.kind}>{orderActions.message.text}</Alert>
        </div>
      )}
      {orderActions.csvMessage && (
        <div className="flex-shrink-0">
          <Alert kind={orderActions.csvMessage.startsWith("Import error") ? "error" : "info"}>
            {orderActions.csvMessage}
          </Alert>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">{mainContent}</div>
    </div>
  );
}

const buildSettingsPanel = ({ filtersOpen, setFiltersOpen, tableState, handleFilterChange }) => {
  if (tableState.tableRows.length === 0) return null;

  const densityMax = COLUMN_DENSITY_LEVELS.length - 1;

  return (
    <div className={`${filtersOpen ? "pb-4" : "pb-2"}`}>
      <div className="pointer-events-auto w-full min-w-full max-w-none rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-expanded={filtersOpen}
            aria-controls="order-settings-panel"
          >
            Settings
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
            />
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={tableState.resetFilters}
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              Reset settings
            </button>
            <ColumnPicker
              options={tableState.columnOptions}
              visibleColumns={tableState.columnPrefs.visibleColumns}
              onToggle={tableState.columnPrefs.toggleColumn}
              onReset={tableState.columnPrefs.resetColumns}
              onSelectAll={tableState.columnPrefs.selectAllColumns}
            />
          </div>
        </div>
        {filtersOpen && (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" id="order-settings-panel">
            <label className="flex flex-col text-sm text-gray-700 gap-1">
              Search
              <input
                type="text"
                value={tableState.filters.search}
                onChange={handleFilterChange("search")}
                placeholder="Any data fragment..."
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <div className="flex flex-col text-sm text-gray-700 gap-1">
              <span>Column density</span>
              <div className="rounded-md border border-gray-300 bg-white px-3 py-2">
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max={densityMax}
                    step="1"
                    value={tableState.density.level}
                    onChange={(event) => tableState.density.setLevel(Number(event.target.value))}
                    className="w-full accent-blue-600"
                    aria-label="Adjust column density"
                    aria-valuetext={tableState.density.label}
                  />
                  <span className="w-24 text-right text-xs font-medium text-gray-600">
                    {tableState.density.label}
                  </span>
                </div>
              </div>
            </div>
            <FilterSelect
              label="Allegro status"
              value={tableState.filters.status}
              onChange={handleFilterChange("status")}
              options={tableState.filterOptions.statusOptions}
            />
            <FilterSelect
              label="Fulfillment status"
              value={tableState.filters.fulfillmentStatus}
              onChange={handleFilterChange("fulfillmentStatus")}
              options={tableState.filterOptions.fulfillmentOptions}
            />
            <FilterSelect
              label="Delivery method"
              value={tableState.filters.deliveryMethod}
              onChange={handleFilterChange("deliveryMethod")}
              options={tableState.filterOptions.deliveryOptions}
            />
            <FilterSelect
              label="Buyer login"
              value={tableState.filters.buyerLogin}
              onChange={handleFilterChange("buyerLogin")}
              options={tableState.filterOptions.buyerLoginOptions}
            />
            <FilterSelect
              label="Allegro Smart!"
              value={tableState.filters.smart}
              onChange={handleFilterChange("smart")}
              options={tableState.filterOptions.smartOptions}
              formatOption={(option) => (option === "true" ? "Yes" : option === "false" ? "No" : option)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, formatOption = (option) => option }) => (
  <label className="flex flex-col text-sm text-gray-700 gap-1">
    {label}
    <select
      value={value}
      onChange={onChange}
      className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">All</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {formatOption(option)}
        </option>
      ))}
    </select>
  </label>
);

const OrdersTable = ({ columnSizing, density, filteredRows, visibleColumnDefs, onRemove, deleting }) => {
  const columnWidths = columnSizing.columnWidths;
  const startColumnResize = columnSizing.startColumnResize;
  const updateColumnWidth = columnSizing.updateColumnWidth;

  const headerPaddingClass = density.headerPaddingClass;
  const cellPaddingClass = density.cellPaddingClass;

  return (
    <table className="min-w-full table-fixed text-left text-sm border border-gray-200">
      <thead>
        <tr className="bg-gray-50 text-gray-700 select-none">
          {visibleColumnDefs.map((column) => {
            const widthValue = columnWidths[column.id];
            const baseClass = `${headerPaddingClass} font-semibold border-b relative group`;
            const style =
              widthValue !== undefined
                ? {
                    width: `${widthValue}px`,
                    minWidth: `${widthValue}px`,
                    maxWidth: `${widthValue}px`,
                  }
                : { minWidth: `${MIN_COLUMN_WIDTH}px` };
            return (
              <th
                key={column.id}
                className={`${baseClass} overflow-hidden border-r border-gray-200 last:border-r-0`}
                style={style}
              >
                <div className="pr-2 w-full overflow-hidden">
                  <span className="block truncate" title={column.label}>
                    {column.label}
                  </span>
                </div>
                <div
                  className="absolute top-0 right-0 flex h-full w-2 cursor-col-resize items-center justify-center"
                  onMouseDown={(event) => startColumnResize(column.id, event)}
                  onDoubleClick={() => updateColumnWidth(column.id, null)}
                >
                  <span className="block h-3/4 w-px bg-gray-300 group-hover:bg-gray-500 transition-colors" />
                </div>
              </th>
            );
          })}
          {(() => {
            const widthValue = columnWidths[ACTIONS_COLUMN_KEY];
            const style =
              widthValue !== undefined
                ? {
                    width: `${widthValue}px`,
                    minWidth: `${widthValue}px`,
                    maxWidth: `${widthValue}px`,
                  }
                : { minWidth: `${MIN_COLUMN_WIDTH}px` };
            return (
              <th
                className={`${headerPaddingClass} font-semibold border-b relative group overflow-hidden border-r border-gray-200`}
                style={style}
              >
                <div className="pr-2 w-full overflow-hidden">
                  <span className="block truncate">Actions</span>
                </div>
                <div
                  className="absolute top-0 right-0 flex h-full w-2 cursor-col-resize items-center justify-center"
                  onMouseDown={(event) => startColumnResize(ACTIONS_COLUMN_KEY, event)}
                  onDoubleClick={() => updateColumnWidth(ACTIONS_COLUMN_KEY, null)}
                >
                  <span className="block h-3/4 w-px bg-gray-300 group-hover:bg-gray-500 transition-colors" />
                </div>
              </th>
            );
          })()}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {filteredRows.map((row, rowIndex) => {
          const orderId = row.order.id;
          return (
            <tr
              key={orderId ?? row.index}
              className={`${rowIndex % 2 === 1 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
            >
              {visibleColumnDefs.map((column) => {
                const entry = row.values[column.id];
                const display = entry?.display ?? "-";
                const title = entry?.strings?.join(", ") || display;
                const widthValue = columnWidths[column.id];
                const cellStyle =
                  widthValue !== undefined
                    ? {
                        width: `${widthValue}px`,
                        minWidth: `${widthValue}px`,
                        maxWidth: `${widthValue}px`,
                      }
                    : { minWidth: `${MIN_COLUMN_WIDTH}px` };
                const className = `${cellPaddingClass} text-gray-800 whitespace-nowrap ${
                  widthValue !== undefined ? "truncate" : ""
                }`;
                return (
                  <td
                    key={column.id}
                    className={`${className} border-r border-gray-200 last:border-r-0`}
                    style={cellStyle}
                    title={title}
                  >
                    {display}
                  </td>
                );
              })}
              <td
                className={`${cellPaddingClass} border-r border-gray-200 last:border-r-0`}
                style={
                  columnWidths[ACTIONS_COLUMN_KEY] !== undefined
                    ? {
                        width: `${columnWidths[ACTIONS_COLUMN_KEY]}px`,
                        minWidth: `${columnWidths[ACTIONS_COLUMN_KEY]}px`,
                        maxWidth: `${columnWidths[ACTIONS_COLUMN_KEY]}px`,
                      }
                    : { minWidth: `${MIN_COLUMN_WIDTH}px` }
                }
              >
                <button
                  onClick={() => orderId && onRemove(orderId)}
                  className="text-red-600 hover:text-red-700 disabled:opacity-50"
                  disabled={deleting || !orderId}
                >
                  Delete
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
